// src/components/Reports/LedgerTable.tsx
import React from "react";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Typography,
	Button,
	TableFooter
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { formatDate } from "../../../utils/calculations";
import { LedgerEntry } from "../../../hooks/useLedger";

interface LedgerTableProps {
	ledgerEntries: LedgerEntry[];
}

const LedgerTable: React.FC<LedgerTableProps> = ({ ledgerEntries }) => {
	const totalAmount = ledgerEntries.reduce(
		(sum, entry) => sum + entry.amount,
		0
	);
	const totalCash = ledgerEntries
		.filter((e) => e.mode === "CASH")
		.reduce((sum, e) => sum + e.amount, 0);
	const totalUPI = ledgerEntries
		.filter((e) => e.mode === "UPI")
		.reduce((sum, e) => sum + e.amount, 0);

	return (
		<>
			{ledgerEntries === null || ledgerEntries.length === 0 ? (
				<p>No ledger entries found.</p>
			) : (
				<Paper elevation={3} sx={{ p: { xs: 1, md: 2 }, mt: 0 }}>
					<Typography variant="h5" gutterBottom>
						Ledger
					</Typography>
					<TableContainer
						component={Paper}
						sx={{ overflowX: "auto" }}
					>
						<Table
							size="small"
							sx={{
								"& .MuiTableCell-root": {
									border: "1px solid rgba(224, 224, 224, 1)"
								},
								"& .MuiTableCell-head": {
									fontWeight: "bold"
								}
							}}
						>
							<TableHead>
								<TableRow>
									<TableCell>Date</TableCell>
									<TableCell>Type</TableCell>
									<TableCell>Customer</TableCell>
									<TableCell>Invoice Number</TableCell>
									<TableCell>Amount</TableCell>
									<TableCell>Mode</TableCell>
									<TableCell align="center">View</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{ledgerEntries.map((entry, index) => (
									<TableRow key={index}>
										<TableCell>
											{formatDate(entry.date)}
										</TableCell>
										<TableCell
											style={{
												color:
													entry.type === "Return"
														? "red"
														: "green"
											}}
										>
											{entry.type}
										</TableCell>
										<TableCell>{entry.details}</TableCell>
										<TableCell>
											{entry.invoiceNumber}
										</TableCell>
										<TableCell
											style={{
												color:
													entry.amount < 0
														? "red"
														: "green"
											}}
										>
											₹{entry.amount.toFixed(2)}
										</TableCell>
										<TableCell>
											{entry.mode ? entry.mode : "-"}
										</TableCell>
										<TableCell align="center">
											<Button
												variant="contained"
												size="small"
												onClick={() =>
													window.open(
														`/invoice?invoiceNumber=${entry.invoiceNumber}`,
														"_blank"
													)
												}
												startIcon={<VisibilityIcon />}
											>
												View
											</Button>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
							<TableFooter>
								<TableRow>
									<TableCell
										colSpan={4}
										sx={{ fontWeight: "bold" }}
										align="right"
									>
										Total Amount
									</TableCell>
									<TableCell
										sx={{
											fontWeight: "bold",
											color:
												totalAmount < 0
													? "red"
													: "green"
										}}
									>
										₹{totalAmount.toFixed(2)}
									</TableCell>
									<TableCell colSpan={2} />
								</TableRow>
								<TableRow>
									<TableCell
										colSpan={4}
										sx={{ fontWeight: "bold" }}
										align="right"
									>
										Total CASH
									</TableCell>
									<TableCell
										sx={{
											fontWeight: "bold",
											color:
												totalCash < 0 ? "red" : "green"
										}}
									>
										₹{totalCash.toFixed(2)}
									</TableCell>
									<TableCell colSpan={2} />
								</TableRow>
								<TableRow>
									<TableCell
										colSpan={4}
										sx={{ fontWeight: "bold" }}
										align="right"
									>
										Total UPI
									</TableCell>
									<TableCell
										sx={{
											fontWeight: "bold",
											color:
												totalUPI < 0 ? "red" : "green"
										}}
									>
										₹{totalUPI.toFixed(2)}
									</TableCell>
									<TableCell colSpan={2} />
								</TableRow>
							</TableFooter>
						</Table>
					</TableContainer>
				</Paper>
			)}
		</>
	);
};

export default LedgerTable;
